/**
 * A prova de que o servidor tem o que o cliente tem — card 1864753210497959381.
 *
 * ## Por que perguntar ao SERVIDOR, e não ao motor de sync
 *
 * O motor guarda bookkeeping próprio (`getPeerPushedClock`) que responderia de graça e sem
 * rede. Foi recusado: se o motor está travado, o bookkeeping dele é coerente **consigo
 * mesmo** e mente junto. É a Regra 8.88 — ground-truth se confirma com comando próprio,
 * nunca com o auto-relato do subsistema que está sob suspeita.
 *
 * ## Por que este arquivo não importa `yjs`
 *
 * O decodificador entra por parâmetro. Não é inversão de dependência por gosto: `yjs` só
 * resolve onde o Affine teve `yarn install`, e um `import` no topo tornaria a decisão inteira
 * — inclusive os três estados do veredito — inexecutável fora do alvo. A afirmação sobre o
 * PROTOCOLO do `yjs` tem teste próprio, com a biblioteca de verdade (Regra 8.49: dublê é uma
 * afirmação sobre o protocolo, e dublê que mente fica verde).
 */

import { compararVetores, type Divergencia } from './comparar-vetores';

export type { Divergencia };
export { compararVetores };

/** O que o `yjs` faz por nós — e só isso. */
export interface Decodificador {
  /** `decodeStateVector` */
  deVetor(bin: Uint8Array): Map<number, number>;
  /** `encodeStateVectorFromUpdate` seguido de `decodeStateVector` */
  deAtualizacao(bin: Uint8Array): Map<number, number>;
}

export type ResultadoDaProva =
  | { veredito: 'em-dia'; docId: string; medidoEm: number }
  | {
      veredito: 'servidor-atrasado';
      docId: string;
      medidoEm: number;
      operacoesFaltando: number;
      clientesAtrasados: number;
    }
  /**
   * ⚠️ O terceiro estado, e ele NÃO colapsa nos outros dois (Regra 8.14).
   *
   * Tratar falha de rede como "o servidor está atrasado" inventaria perda de dado — o pior
   * falso positivo que esta entrega pode produzir, porque manda a pessoa procurar um problema
   * que não existe e, na vez seguinte, ela não acredita no aviso verdadeiro.
   */
  | { veredito: 'nao-verificavel'; docId: string; medidoEm: number; porque: string };

export function caminhoDoDoc(workspaceId: string, docId: string): string {
  return `/api/workspaces/${encodeURIComponent(workspaceId)}/docs/${encodeURIComponent(docId)}`;
}

export interface OpcoesDaProva {
  workspaceId: string;
  docId: string;
  /** o vetor de estado do documento como ele está NESTA tela, já codificado */
  vetorLocal: Uint8Array;
  decodificador: Decodificador;
  /** injetável: o teste não fala com rede; o navegador manda o cookie de sessão */
  buscar?: typeof fetch;
  agora?: () => number;
  sinal?: AbortSignal;
}

/**
 * Pergunta ao servidor e compara.
 *
 * A rota é `GET /api/workspaces/:id/docs/:guid` (`core/workspaces/controller.ts`), que é
 * `@Public()` mas passa por `assert('Doc.Read')` — autentica pelo cookie de sessão que o
 * navegador já tem. É a ÚNICA superfície de leitura de documento que o navegador alcança:
 * todo o `/rpc/*` é `@Internal()` e exige o segredo do servidor.
 *
 * ⚠️ Custo declarado: essa rota chama `recordDocView` para documento que não é o raiz, então
 * a prova infla a analítica de visualizações do documento aberto. É o preço de perguntar ao
 * servidor em vez de acreditar no motor — e é por isso que o intervalo é folgado.
 */
export async function provarContraOServidor(
  opcoes: OpcoesDaProva
): Promise<ResultadoDaProva> {
  const buscar = opcoes.buscar ?? globalThis.fetch;
  const medidoEm = (opcoes.agora ?? Date.now)();
  const docId = opcoes.docId;
  const naoSei = (porque: string): ResultadoDaProva => ({
    veredito: 'nao-verificavel',
    docId,
    medidoEm,
    porque,
  });

  if (typeof buscar !== 'function') {
    return naoSei('este ambiente não tem fetch');
  }

  let resposta: Response;
  try {
    resposta = await buscar(caminhoDoDoc(opcoes.workspaceId, docId), {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/octet-stream' },
      signal: opcoes.sinal,
    });
  } catch (erro) {
    return naoSei(`não consegui falar com o servidor (${mensagem(erro)})`);
  }

  if (!resposta.ok) {
    // 404 aqui NÃO é "o servidor não tem nada": é também o que responde um documento que
    // nunca subiu. São coisas diferentes e esta rota não as separa, então a resposta honesta
    // é não saber.
    return naoSei(`o servidor respondeu ${resposta.status}`);
  }

  let binario: Uint8Array;
  try {
    binario = new Uint8Array(await resposta.arrayBuffer());
  } catch (erro) {
    return naoSei(`não consegui ler a resposta (${mensagem(erro)})`);
  }

  if (binario.length === 0) {
    return naoSei('o servidor devolveu um documento vazio');
  }

  let divergencia: Divergencia;
  try {
    divergencia = compararVetores(
      opcoes.decodificador.deVetor(opcoes.vetorLocal),
      opcoes.decodificador.deAtualizacao(binario)
    );
  } catch (erro) {
    return naoSei(`não consegui comparar os vetores (${mensagem(erro)})`);
  }

  if (divergencia.emDia) {
    return { veredito: 'em-dia', docId, medidoEm };
  }

  return {
    veredito: 'servidor-atrasado',
    docId,
    medidoEm,
    operacoesFaltando: divergencia.operacoesFaltando,
    clientesAtrasados: divergencia.clientesAtrasados,
  };
}

function mensagem(erro: unknown): string {
  return erro instanceof Error ? erro.message : String(erro);
}
